import {
  Reflection,
  ReflectionData,
  ReflectionTimeRange
} from "@/entities/reflection";
import dayjs from "dayjs";

type ReflectionDataWithTimeRange = Omit<ReflectionData, "timeRange"> & {
  timeRange: ReflectionTimeRange;
};

export type ReflectionWithTimeRange = Omit<Reflection, "data"> & {
  data: ReflectionDataWithTimeRange;
};

export function fillMissingTimeRange(
  reflection: Reflection | ReflectionWithTimeRange
): ReflectionWithTimeRange {
  if (reflection.data.timeRange) {
    return reflection as ReflectionWithTimeRange;
  }

  const datePart = reflection.data.uid.slice(0, 10); // YYYY-MM-DD 部分だけ抜き取る
  const start = dayjs(datePart).startOf("day").unix();
  const end = dayjs(datePart).endOf("day").unix();

  const timeRange = {
    start,
    end
  };

  return {
    ...reflection,
    data: {
      ...reflection.data,
      timeRange
    }
  };
}

export function validateTimeRange(start: number, end: number): boolean {
  return start < end;
}

export function isFutureDate(unix: number): boolean {
  return dayjs.unix(unix).diff(dayjs(), "day") >= 1;
}

export function generateReflectionUid(
  reflections: (Reflection | ReflectionWithTimeRange)[],
  date: string
): string {
  /*
    dateは、"2024-12-01"のような形で渡ってくる
    各uidは、"2024-12-01", "2024-12-01-1", "2024-12-01-2"のように定義するものとする
    */
  if (!reflections.length) return date;
  const uidSet = new Set(reflections.map(r => r.data.uid));
  if (!uidSet.has(date)) return date;

  let i = 1;
  while (uidSet.has(`${date}-${i}`)) i++;
  return `${date}-${i}`;
}

export function normalizeUid(date: string): string {
  // "2024-12-22-1"のようなデータが渡ってくる前提
  const parts = date.split("-").map(p => String(p));
  if (parts.length === 3) {
    return date;
  } else if (parts.length === 4) {
    return `${parts[0]}-${parts[1]}-${parts[2]}`;
  } else {
    throw new Error(`Invalid date format -- ${date}`);
  }
}

export function getUnixFromUid(date: string): number {
  // "2024-12-22-1"のようなデータが渡ってくる前提
  if (!date) return 0;
  const _date = (() => {
    try {
      return normalizeUid(date);
    } catch (_) {
      return "";
    }
  })();
  if (!_date) return 0;

  return dayjs(_date).startOf("day").unix();
}

export function sortReflectionsByUid(
  reflections: (Reflection | ReflectionWithTimeRange)[],
  option = { asc: true }
) {
  if (!reflections || !Array.isArray(reflections) || !reflections.length) {
    return [];
  }
  const { asc } = option;
  const _reflections = [...reflections];
  return _reflections.sort((a, b) => {
    const diff = getUnixFromUid(a.data.uid) - getUnixFromUid(b.data.uid);
    if (diff !== 0) return asc ? diff : diff * -1;
    return a.data.updatedAt - b.data.updatedAt;
  });
}
